import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { syncBackendThemes } from "../hooks/useColorTheme";

const RestaurantContext = createContext(null);

export function RestaurantProvider({ children }) {
  const [restaurants, setRestaurants] = useState([]);
  const [currentRestaurant, setCurrentRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchRestaurants = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getRestaurants();
      setRestaurants(data);
      // Reconcile per-restaurant theme overrides with backend `branding.theme`
      syncBackendThemes(data);
      
      // Set current restaurant from localStorage or first one
      const savedId = localStorage.getItem("currentRestaurantId");
      if (savedId) {
        const saved = data.find((r) => r.restaurant_id === savedId);
        if (saved) {
          setCurrentRestaurant(saved);
        } else if (data.length > 0) {
          setCurrentRestaurant(data[0]);
          localStorage.setItem("currentRestaurantId", data[0].restaurant_id);
        }
      } else if (data.length > 0) {
        setCurrentRestaurant(data[0]);
        localStorage.setItem("currentRestaurantId", data[0].restaurant_id);
      }
    } catch (error) {
      console.error("Failed to fetch restaurants:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRestaurants();
  }, [fetchRestaurants]);

  // Auto-recovery: if any API call returns 403/404 for the currently selected
  // restaurant (e.g. it was deleted/wiped from another tab or the user lost
  // access), refresh the list and fall back to the first valid one. Throttled
  // by stale_id so we don't spam refreshes when many endpoints fail at once.
  useEffect(() => {
    let lastHandledId = null;
    let lastHandledAt = 0;
    const onLost = async (e) => {
      const lostId = e?.detail?.restaurant_id;
      if (!lostId) return;
      // Only react if the lost id matches the active one (or there's nothing
      // currently selected and the lost id is what's in localStorage).
      const activeId = currentRestaurant?.restaurant_id || localStorage.getItem("currentRestaurantId");
      if (lostId !== activeId) return;
      const now = Date.now();
      if (lastHandledId === lostId && (now - lastHandledAt) < 8000) return;
      lastHandledId = lostId;
      lastHandledAt = now;
      try {
        const fresh = await api.getRestaurants();
        setRestaurants(fresh);
        const stillValid = fresh.find((r) => r.restaurant_id === lostId);
        if (stillValid) {
          // We do have access — just refresh the cached object.
          setCurrentRestaurant(stillValid);
        } else if (fresh.length > 0) {
          setCurrentRestaurant(fresh[0]);
          localStorage.setItem("currentRestaurantId", fresh[0].restaurant_id);
          // eslint-disable-next-line no-console
          console.info(`[RestaurantContext] Stale restaurant ${lostId} replaced by ${fresh[0].restaurant_id}`);
        } else {
          setCurrentRestaurant(null);
          localStorage.removeItem("currentRestaurantId");
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[RestaurantContext] Failed to recover from lost restaurant:", err);
      }
    };
    window.addEventListener("restaurant-access-lost", onLost);
    return () => window.removeEventListener("restaurant-access-lost", onLost);
  }, [currentRestaurant]);

  const selectRestaurant = (restaurant) => {
    setCurrentRestaurant(restaurant);
    localStorage.setItem("currentRestaurantId", restaurant.restaurant_id);
  };

  const addRestaurant = async (data) => {
    const newRestaurant = await api.createRestaurant(data);
    setRestaurants((prev) => [...prev, newRestaurant]);
    setCurrentRestaurant(newRestaurant);
    localStorage.setItem("currentRestaurantId", newRestaurant.restaurant_id);
    return newRestaurant;
  };

  const updateRestaurant = async (id, data) => {
    const updated = await api.updateRestaurant(id, data);
    setRestaurants((prev) =>
      prev.map((r) => (r.restaurant_id === id ? updated : r))
    );
    if (currentRestaurant?.restaurant_id === id) {
      setCurrentRestaurant(updated);
    }
    return updated;
  };

  return (
    <RestaurantContext.Provider
      value={{
        restaurants,
        currentRestaurant,
        loading,
        selectRestaurant,
        addRestaurant,
        updateRestaurant,
        refreshRestaurants: fetchRestaurants,
      }}
    >
      {children}
    </RestaurantContext.Provider>
  );
}

export function useRestaurant() {
  const context = useContext(RestaurantContext);
  if (!context) {
    throw new Error("useRestaurant must be used within RestaurantProvider");
  }
  return context;
}
